import React, { FunctionComponent, useRef, useState } from 'react'
import { Typography, Button, Input, Space, Table, InputRef, Tooltip } from 'antd'
import { useLoaderData, useRouter } from '@tanstack/react-router'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import type { ColumnType } from 'antd/es/table'
import type { FilterConfirmProps } from 'antd/es/table/interface'
import { formatTimestamp, fromNow } from '../../../utils/time'
import { AssettrakAccess, Vehicle } from 'server'
import { arrayToMap } from '../../../utils/data'

interface DataType {
  id: number
  access_code: string
  allowed_agencies: string
  notes: string
  created_date: string
  last_login: string
}

type DataIndex = keyof DataType

const AccessTable: FunctionComponent<any> = () => {
  const { assettrakAccess, agencies, vehicles } = useLoaderData({ from: '/admin/_layout/access/' })

  const agencyMap = arrayToMap(agencies, 'id')

  const router = useRouter()

  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<DataType> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false })
              setSearchText((selectedKeys as string[])[0])
              setSearchedColumn(dataIndex)
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) => {
      if (!record[dataIndex]) return false
      return record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase())
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      )
  })

  const columns = [
    {
      title: 'Access Code',
      dataIndex: 'access_code',
      key: 'access_code',
      render: (text: string, record: any, index: number) => {
        return <Typography.Text ellipsis={true}>{text}</Typography.Text>
      },
      ...getColumnSearchProps('access_code')
    },
    {
      title: 'Allowed Agencies',
      dataIndex: 'allowed_agencies',
      key: 'allowed_agencies',
      render: (text: string, record: any, index: number) => {
        const agencyIds = text.split(',').map((_: string) => parseInt(_))
        const count = agencyIds.length
        return (
          <Tooltip
            title={
              <ul>
                {agencyIds.map((agencyId: number, index: number) => (
                  <li key={`${record.id}-${agencyId}-${index}`}>{agencyMap.get(agencyId)?.name}</li>
                ))}
              </ul>
            }
          >
            <Typography.Text style={{ maxWidth: '300px' }} ellipsis={true}>
              {count}
            </Typography.Text>
          </Tooltip>
        )
      }
    },
    {
      title: 'Agency',
      dataIndex: 'agency',
      key: 'agency',
      render: (text: string, record: any, index: number) => {
        const agency = agencyMap.get(record.agency)
        return (
          <Tooltip title={agency?.name}>
            <Typography.Text ellipsis={true}>{agency ? agency.name : '---'}</Typography.Text>
          </Tooltip>
        )
      },
      sorter: (a: any, b: any) => {
        const agencyA = agencyMap.get(a.agency)
        const agencyB = agencyMap.get(b.agency)
        if (!agencyA || !agencyB) return 0
        return agencyA.name.localeCompare(agencyB.name)
      }
    },
    {
      title: '# of Vehicles',
      key: 'vehicles',
      render: (text: string, record: AssettrakAccess, index: number) => {
        const agencyIds = record.allowed_agencies.split(',').map((_: string) => parseInt(_))
        const vehicleCount = getVehicleCount(agencyIds)
        return <Typography.Text>{vehicleCount}</Typography.Text>
      },
      sorter: (a: any, b: any) => {
        const agencyIdsA = a.allowed_agencies.split(',').map((_: string) => parseInt(_))
        const vehicleCountA = getVehicleCount(agencyIdsA)
        const agencyIdsB = b.allowed_agencies.split(',').map((_: string) => parseInt(_))
        const vehicleCountB = getVehicleCount(agencyIdsB)
        return vehicleCountA - vehicleCountB
      }
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (text: string, record: any, index: number) => {
        return <Tooltip title={text}>
          <Typography.Text>{formatTimestamp(text)} ({fromNow(text)})</Typography.Text>
        </Tooltip>
      },
      sorter: (a: any, b: any) => {
        return a.last_login.localeCompare(b.last_login)
      }
    }
  ]

  const getVehicleCount = (ids: number[]): number =>
    vehicles.filter((_: Vehicle) => ids.includes(_.agency_id)).length

  return (
    <Table
      style={{
        cursor: 'pointer',
        margin: '12px'
      }}
      pagination={{ pageSize: 100 }}
      onRow={(record, rowIndex) => {
        return {
          onClick: (event) => {
            const url = router.buildLocation({ to: `/admin/access/${record.id}` }).href;
            window.open(url, '_blank'); // Opens in a new tab
          }, // click row
        }
      }}
      bordered={true}
      size={'small'}
      dataSource={assettrakAccess}
      columns={columns}
      rowKey={'id'}
      tableLayout={'fixed'}
    />
  )
}

export default AccessTable
